/**
 * src/components/ConverterApp.tsx — the core screen: header, toolbar, three-panel shell, footer.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/components/ConverterApp.tsx` (whole file, 322 lines).
 * Spec:   `docs/spec/06-frontend-components.md` §4 (state, derived values, handlers, layout),
 *         §5 (editor/preview), §6.2 (filename derivation), §7 (save/load round trip),
 *         §15 D-2, D-3, D-14, D-21, D-22, D-23; §16 ranks 1, 2, 4, 11, 14, 15, 16, 17.
 *
 * This file owns the state that the whole screen reads, and it is where six DF-22 defects live.
 * Read the block below before changing a single line — several of the defects look like omissions
 * and are not.
 *
 * ═══ DF-22 RANK 1 (spec D-22) — ✅ PARTLY FIXED 2026-08-09, owner-approved ════════════════════
 * **The first fix applied to this screen.** Everything else below is still reproduced bug-for-bug.
 *
 * A dirty flag (`isDirty`) and a `beforeunload` listener now exist, so refreshing or closing the tab
 * with unsaved edits raises the browser's "Leave site?" prompt instead of destroying the work
 * silently. See the block on `isDirty` below for what sets and clears it, and why only typing does.
 *
 * **Still absent, deliberately — the owner chose the smaller half of the proposed fix:** no autosave,
 * no localStorage draft, no dirty *indicator* in the UI. A crash or a power cut still loses the text;
 * this converts silent loss into a question, it does not make the work durable.
 *
 * ═══ DF-22 RANK 2 (spec D-2) — SAVING A LOADED DOCUMENT CREATES A DUPLICATE. REPRODUCED ══════
 * Two halves, both here, and BOTH are required for the defect to appear:
 *   (a) `handleLoadDocument` receives `documentId` and throws it away (`:116-122`). Nothing in
 *       this component ever holds a `loadedDocumentId`. It also ignores `htmlContent`.
 *   (b) The save dialogs are rendered with content props only (`:255-259`) — `documentId`,
 *       `initialTitle`, `initialPreambleId`, `initialFolderId` are never passed, so
 *       `SaveDocumentDialog` always takes its "create new" branch and its whole `PUT` path is
 *       dead code. Same for `SavePreambleDialog`/`preambleId`.
 * Result: load a document, edit it, save → a SECOND row, or a "Document Already Exists" prompt.
 * Do NOT add `loadedDocumentId` state and do NOT thread anything into the dialogs.
 *
 * ═══ DF-22 RANK 4 (spec D-3) — CONVERSION IS NOT DEBOUNCED. REPRODUCED ═══════════════════════
 * `fullLatex` and `{ html, css }` are plain `useMemo`s (`:112-113`), so the entire pipeline runs
 * **synchronously on the main thread on every character typed**, and its output is handed to
 * `HtmlPreview`, which rewrites its iframe each time (DF-22 rank 4's other half — see that file).
 * No debounce, no `useDeferredValue`, no Web Worker, no "last good output" cache.
 *
 * ═══ DF-22 RANK 11 (spec D-21) — CLEAR AND UPLOAD DESTROY WORK WITHOUT CONFIRMATION ══════════
 * `handleClear` (`:184-193`) wipes the active tab with no dialog and no undo. `handleFileUpload`'s
 * first branch (`:197-202`) additionally does `setPreamble('')` — uploading a full document
 * silently empties a loaded preamble. Both reproduced; no `AlertDialog`, no toast action.
 *
 * ═══ DF-22 RANK 14 — DOWNLOAD FILENAMES ARE HARD-CODED. REPRODUCED ═══════════════════════════
 * HTML is always `document.html` (`:138`) and CSS always `latex-styles.css` (`:149`), even when a
 * named document is loaded. Only the `.tex` path derives a name (`:162-167`) — and it does not
 * sanitise it, so a title containing `/`, `\`, `:` or a newline goes straight into `a.download`.
 * The `.tex` suffix test is `baseName.toLowerCase().endsWith('.tex')` (`:164`), so `MyDoc.TEX`
 * keeps its uppercase extension. All three downloads create a detached `<a>`, never append it to
 * the DOM, and call `URL.revokeObjectURL` immediately after `.click()` (`:139-140`, `:150-152`,
 * `:168-170`) — reliable in Chromium, historically fragile elsewhere. Reproduced as-is.
 *
 * ═══ DF-22 RANK 15 (spec D-23) — NO KEYBOARD SHORTCUTS. REPRODUCED ═══════════════════════════
 * No Ctrl/Cmd+S, no Ctrl/Cmd+O, no shortcut handling of any kind. Every save is a mouse round trip.
 *
 * ═══ DF-22 RANK 16 (spec D-12) — THE DEFAULT PREAMBLE IS NEVER AUTO-APPLIED. REPRODUCED ══════
 * `preamble` starts `''` and nothing reads `is_default`. There is deliberately no mount effect
 * that loads the default preamble into an empty preamble tab.
 *
 * ═══ DF-22 RANK 17 (spec D-14) — PLACEHOLDERS SHOW LITERAL `\n`. REPRODUCED ══════════════════
 * The two `placeholder=` values (`:295`, `:298`) are **JSX attribute string literals**, and JSX
 * does not process escape sequences in those — so `\n` and `\\` reach the DOM as the characters
 * backslash-n and backslash-backslash, and each placeholder renders as one long line with visible
 * escapes. They are written below in exactly that form. Rewriting either as `placeholder={`…`}`
 * with real newlines would silently FIX rank 17. Leave the plain double-quoted attributes alone.
 *
 * ═══ ALSO REPRODUCED HERE ═══════════════════════════════════════════════════════════════════
 * • **The round trip is lossy and asymmetric** (spec §7.1). `Download LaTeX` exports
 *   preamble + `\n\n` + document (`:156`), while `Save Document` stores the document tab only
 *   (`:257`). Re-loading a saved document restores the body; the preamble tab keeps whatever was
 *   already there. `preamble_id` is a label — nothing reads it back on load.
 * • **No split on load.** `handleLoadDocument` deliberately does not split at `\begin{document}`;
 *   the splitting version was removed twice in the source (`ed53ef4`, `9e8c663`) and four project
 *   docs still wrongly describe it (spec C-1). Do not reintroduce it.
 * • **The upload classifier is textual and order-dependent** (`:195-214`, spec §7.3). It tests the
 *   raw, un-stripped file with `String.includes`, so `% \begin{document}` inside a comment or
 *   inside `\verb` classifies as a full document and wipes the preamble (F1/F2). And it tests only
 *   for `\documentclass`, which the real `preambuleComTrad-20032026.tex` does not contain — so the
 *   project's own reference preamble uploads into the **document** tab (F3).
 * • **Two toasts per upload** (spec §6.1): `Toolbar` toasts `'File loaded successfully'` and
 *   `handleFileUpload` toasts again.
 * • **`SAMPLE_PREAMBLE` / `SAMPLE_DOCUMENT` are not ported.** They are declared and never used in
 *   the source (`:16-101`; spec §4.1, §13) and the editors start empty. Porting them as dead
 *   constants would break `noUnusedLocals`; porting them as seed content would change behaviour.
 *   The observable result — two empty editors on first paint — is identical either way.
 *
 * ═══ DF-20 — `html_content` NO LONGER EXISTS SERVER-SIDE ════════════════════════════════════
 * Owner-approved 2026-07-29. `htmlContent={html}` is still passed to `SaveDocumentDialog` because
 * that prop is part of the ported component contract (`SaveDocumentDialog.tsx:35-42`), but the
 * dialog must not put it in the request body — `CreateDocumentInput` has no such field. This is
 * the one place the port stops short of the source, and it is unobservable: the value was written,
 * fetched, passed one call deep into `handleLoadDocument`, and discarded unread.
 *
 * ═══ TOASTS ════════════════════════════════════════════════════════════════════════════════
 * Every toast in this file is **sonner** (`ConverterApp.tsx:11`) — unchanged by the DF-22 rank 9
 * deviation, which only affects the files that used shadcn's `useToast`. All strings verbatim.
 */
import { useState, useMemo, useCallback, useEffect } from 'react'
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LatexEditor } from './LatexEditor'
import { HtmlPreview } from './HtmlPreview'
import { Toolbar } from './Toolbar'
import { EnhancedSavedDocuments } from './EnhancedSavedDocuments'
import { SaveDocumentDialog } from './SaveDocumentDialog'
import { SavePreambleDialog } from './SavePreambleDialog'
import { convertLatexToHtml, generateFullHtml } from '@/lib/converter'
import { toast } from 'sonner'
import { FileText, Settings, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

export function ConverterApp() {
  const { user } = useAuth()
  // Spec §4.1 — five pieces of state, all local, none persisted. `activeTab` starts on 'document'.
  const [preamble, setPreamble] = useState('')
  const [docContent, setDocContent] = useState('')

  /**
   * ═══ DF-22 RANK 1 — UNSAVED-CHANGES PROTECTION. Owner-approved 2026-08-09. ════════════════════
   * The **first fix applied to this screen**; everything else here is still reproduced bug-for-bug.
   *
   * Before this, typing a paragraph of scripture and refreshing the tab lost it silently — no
   * prompt, no draft, nothing. Ranked first in DF-22 because it destroys translation work.
   *
   * **Only typing marks the document dirty.** The flag is set by {@link handlePreambleChange} and
   * {@link handleDocChange}, which are the editors' `onChange`, and by nothing else. Every other
   * write to `preamble`/`docContent` is the app itself — loading, clearing, uploading — and each of
   * those *clears* the flag instead. Setting it on every `setDocContent` would make the warning fire
   * after merely opening a document, and a warning that cries wolf is worse than no warning: people
   * learn to click through it, including the one time it was real.
   *
   * A save clears it too, via the `onSaved` callback the two dialogs now accept.
   *
   * **What this deliberately does NOT do** (the owner chose the smaller half of the proposed fix):
   * there is no autosave and no localStorage draft, so a browser crash or a power cut still loses
   * the text. This turns silent loss into a question; it does not make the work durable.
   */
  const [isDirty, setIsDirty] = useState(false)

  /**
   * The editors' `onChange`. Wrapping the setters here — rather than tracking a copy of the text and
   * diffing it — keeps the 487 KB *Florilège* out of a second string and off every render.
   */
  const handlePreambleChange = useCallback((value: string) => {
    setPreamble(value)
    setIsDirty(true)
  }, [])

  const handleDocChange = useCallback((value: string) => {
    setDocContent(value)
    setIsDirty(true)
  }, [])

  /**
   * The browser's own "Leave site?" prompt. `preventDefault()` is what modern browsers require;
   * `returnValue` is the legacy form and is harmless to keep.
   *
   * The message is the browser's and cannot be customised — that is a deliberate anti-abuse rule in
   * every engine, not something to work around. Registered only while `isDirty`, so a clean session
   * never pays for the listener and never sees a prompt.
   */
  useEffect(() => {
    if (!isDirty) return

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [isDirty])
  const [activeTab, setActiveTab] = useState<'preamble' | 'document'>('document')
  const [loadedDocumentName, setLoadedDocumentName] = useState<string | null>(null)
  const [loadedPreambleName, setLoadedPreambleName] = useState<string | null>(null)

  // Merge preamble and document for conversion
  // Exact join: preamble, \n, \n, document (`ConverterApp.tsx:112`). This is "Mode A" — because the
  // preamble is concatenated ahead of the body, macro expansion (S7) rewrites every domain macro
  // into generic LaTeX before the hand-written Bahá'í handlers ever run, so those handlers never
  // fire and the domain stylesheet is dead in normal use (DF-15). That is the shipped behaviour.
  const fullLatex = useMemo(() => `${preamble}\n\n${docContent}`, [preamble, docContent])
  // DF-22 rank 4 — synchronous, un-debounced, on every keystroke. Do not defer or debounce.
  const { html, css } = useMemo(() => convertLatexToHtml(fullLatex), [fullLatex])

  // Handle loading a document from SavedDocuments
  //
  // DF-22 rank 2 — `_htmlContent` and `_documentId` are received and DISCARDED. The underscores
  // exist only to satisfy `noUnusedParameters`/`no-unused-vars`, which the source did not run
  // (`strict: false`); the signature, the argument order and the runtime behaviour are unchanged.
  // Threading `_documentId` into state is the fix, and the fix is not authorised.
  const handleLoadDocument = useCallback(
    (
      latexContent: string,
      _htmlContent: string,
      _documentId?: number,
      documentTitle?: string,
    ) => {
      // Always load content into the document tab directly
      // We no longer try to split automatically, as this causes issues with documents that include their own preamble/setup
      setDocContent(latexContent)
      setLoadedDocumentName(documentTitle || null)
      // DF-22 rank 1 — the app set this text, not the translator. Nothing is unsaved yet.
      setIsDirty(false)
      // Note: the preamble tab is NOT touched and the active tab is NOT switched (spec §4.3).
      toast.success('Document loaded successfully')
    },
    [],
  )

  // Handle loading a preamble
  const handleLoadPreamble = useCallback((preambleContent: string, preambleName?: string) => {
    setPreamble(preambleContent)
    setLoadedPreambleName(preambleName || null)
    setIsDirty(false) // DF-22 rank 1
    setActiveTab('preamble')
    toast.success('Preamble loaded successfully')
  }, [])

  const handleDownloadHtml = useCallback(() => {
    const fullHtml = generateFullHtml(html, css)
    const blob = new Blob([fullHtml], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    // DF-22 rank 14 — hard-coded; `loadedDocumentName` is ignored on this path.
    a.download = 'document.html'
    a.click()
    URL.revokeObjectURL(url)
    toast.success('HTML file downloaded')
  }, [html, css])

  const handleDownloadCss = useCallback(() => {
    const blob = new Blob([css], { type: 'text/css' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    // DF-22 rank 14 — hard-coded.
    a.download = 'latex-styles.css'
    a.click()
    URL.revokeObjectURL(url)
    toast.success('CSS file downloaded')
  }, [css])

  const handleDownloadLatex = useCallback(() => {
    // Spec §7.1 — this exports preamble + '\n\n' + document, while Save Document persists the
    // document tab alone. The asymmetry is the source's and is reproduced.
    const blob = new Blob([fullLatex], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url

    // Prefer the currently loaded document name from the tab, fallback to a sensible default
    let baseName = loadedDocumentName || 'document.tex'
    // Ensure the filename ends with .tex
    // DF-22 rank 14 — no sanitisation of '/', '\', ':' or newlines; the case-insensitive test
    // leaves 'MyDoc.TEX' uppercase.
    if (!baseName.toLowerCase().endsWith('.tex')) {
      baseName = `${baseName}.tex`
    }

    a.download = baseName
    a.click()
    URL.revokeObjectURL(url)
    toast.success(`LaTeX file "${baseName}" downloaded`)
  }, [fullLatex, loadedDocumentName])

  const handleCopyHtml = useCallback(async () => {
    const fullHtml = generateFullHtml(html, css)
    try {
      await navigator.clipboard.writeText(fullHtml)
      toast.success('HTML copied to clipboard')
    } catch {
      // The real reason is discarded — clipboard denial and a serialisation failure look the same.
      toast.error('Failed to copy')
    }
  }, [html, css])

  const handleClear = useCallback(() => {
    // DF-22 rank 11 — destructive, no confirmation, no undo, fires even on an empty tab.
    if (activeTab === 'preamble') {
      setPreamble('')
      setLoadedPreambleName(null)
    } else {
      setDocContent('')
      setLoadedDocumentName(null)
    }
    // DF-22 rank 1 — there is nothing left to lose, so nothing to warn about.
    setIsDirty(false)
    toast.success(`${activeTab === 'preamble' ? 'Preamble' : 'Document'} cleared`)
  }, [activeTab])

  const handleFileUpload = useCallback((content: string) => {
    // Try to detect if it's a preamble or document
    //
    // Spec §7.3 — `String.includes` on the raw file: a commented-out or \verb-quoted
    // `\begin{document}` still matches (F1/F2), and a preamble without `\documentclass` — which is
    // exactly what `preambuleComTrad-20032026.tex` is — falls through to the document tab (F3).
    // Order matters; do not reorder the branches.
    if (content.includes('\\begin{document}')) {
      // Load the full document without splitting
      setDocContent(content)
      // DF-22 rank 11 — the loaded preamble is destroyed here, silently.
      setPreamble('') // Clear preamble to avoid duplication
      setActiveTab('document')
      toast.success('Full document loaded')
    } else if (content.includes('\\documentclass')) {
      // It's a preamble
      setPreamble(content)
      setActiveTab('preamble')
      toast.success('Preamble loaded')
    } else {
      // It's document content
      setDocContent(content)
      setActiveTab('document')
      toast.success('Document loaded')
    }
    // DF-22 rank 1 — the editor now mirrors a file that still exists on disk, so there is nothing
    // unrecoverable pending. Note any typing from before the upload is already gone: replacing the
    // tabs without confirmation is rank 11, which is NOT fixed here.
    setIsDirty(false)
  }, [])

  return (
    // Spec §4.4 — `h-screen flex flex-col`: the app never scrolls at page level; only the panels do.
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">Lx</span>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">LaTeX → HTML</h1>
            <p className="text-xs text-muted-foreground">Accurate document conversion</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {user?.role === 'admin' && (
            <Link
              to="/admin"
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              <ShieldCheck className="w-4 h-4" />
              Admin
            </Link>
          )}
          {/*
            Spec §9.1 — this is the SECOND mount of `EnhancedSavedDocuments`. In `headerOnly` mode
            it renders only the "Logged in as / <username> / Logout" chip, but its full hook set
            still runs above the early return (DF-22 rank 18 / spec D-16). Both `onLoad*` callbacks
            are passed and are unreachable in this mode; that is the source's prop wiring.
          */}
          <EnhancedSavedDocuments
            onLoadDocument={handleLoadDocument}
            onLoadPreamble={handleLoadPreamble}
            headerOnly={true}
          />
        </div>
      </header>

      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-border">
        <Toolbar
          onFileUpload={handleFileUpload}
          onDownloadHtml={handleDownloadHtml}
          onDownloadCss={handleDownloadCss}
          onDownloadLatex={handleDownloadLatex}
          onCopyHtml={handleCopyHtml}
          onClear={handleClear}
        />
        <div className="flex items-center gap-2 px-4">
          {/*
            DF-22 rank 2 — content props ONLY. No `preambleId`, no `documentId`, no `initialTitle`,
            no `initialPreambleId`, no `initialFolderId`. This is the omission that makes both
            dialogs' update branches dead code and turns every save of a loaded item into a new row.
            `htmlContent` is still passed (ported prop contract) but must never reach the wire — DF-20.
          */}
          <SavePreambleDialog preambleContent={preamble} onSaved={() => setIsDirty(false)} />
          <SaveDocumentDialog latexContent={docContent} htmlContent={html} onSaved={() => setIsDirty(false)} />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {/*
          Spec §4.4 / §16 "Layout stability requirement" — the three-panel 20 / 40 / 50 shell.
          The literals below are the source's (`ConverterApp.tsx:267`, `:280`, `:305`) and both apps
          run react-resizable-panels ^2.1.9, which normalises the 110 total identically in each.
          Writing pre-normalised numbers (the spec floats 20/40/40 or 18/36/46) would CHANGE the
          rendered widths away from the shipped app — see the discrepancy note in the port report.
        */}
        <ResizablePanelGroup direction="horizontal">
          {/* Sidebar with Saved Documents */}
          <ResizablePanel defaultSize={20} minSize={15} maxSize={30}>
            <div className="h-full p-4 bg-muted/30 flex flex-col">
              <EnhancedSavedDocuments
                onLoadDocument={handleLoadDocument}
                onLoadPreamble={handleLoadPreamble}
                headerOnly={false}
              />
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle className="bg-border hover:bg-primary/50 transition-colors" />

          {/* Editor Panel */}
          <ResizablePanel defaultSize={40} minSize={25}>
            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as 'preamble' | 'document')}
              className="h-full flex flex-col"
            >
              <div className="px-4 pt-3 pb-2 border-b border-border bg-card/50">
                <TabsList className="inline-flex">
                  {/* Dynamic filenames — spec §4.4. Preamble tab first, document tab second. */}
                  <TabsTrigger value="preamble" className="flex items-center gap-2 text-xs">
                    <Settings className="w-3 h-3" />
                    {loadedPreambleName || 'preamble.tex'}
                  </TabsTrigger>
                  <TabsTrigger value="document" className="flex items-center gap-2 text-xs">
                    <FileText className="w-3 h-3" />
                    {loadedDocumentName || 'document.tex'}
                  </TabsTrigger>
                </TabsList>
              </div>
              {/*
                DF-22 rank 17 — the two `placeholder` values below are JSX ATTRIBUTE LITERALS, not
                expressions. JSX performs no escape processing on them, so `\n` and `\\` reach the
                textarea as literal characters and the hint renders as one line of visible escapes.
                Converting either to a template literal fixes rank 17. Do not.
              */}
              <TabsContent value="preamble" className="flex-1 m-0 overflow-hidden">
                <LatexEditor value={preamble} onChange={handlePreambleChange} placeholder="% Enter your preamble here...\n\\documentclass{article}\n\\usepackage{...}" />
              </TabsContent>
              <TabsContent value="document" className="flex-1 m-0 overflow-hidden">
                <LatexEditor value={docContent} onChange={handleDocChange} placeholder="\\begin{document}\n\n% Your document content here...\n\n\\end{document}" />
              </TabsContent>
            </Tabs>
          </ResizablePanel>

          <ResizableHandle withHandle className="bg-border hover:bg-primary/50 transition-colors" />

          {/*
            The preview panel is the widest by default (50) precisely so the generated stylesheet's
            body padding leaves room for `\Nverset` verse numbers floated into the LEFT margin.
            `HtmlPreview` adds no wrapper of its own — see its header comment.
          */}
          <ResizablePanel defaultSize={50} minSize={30}>
            <HtmlPreview html={html} css={css} />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Footer */}
      <footer className="px-6 py-2 border-t border-border bg-card flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {fullLatex.length.toLocaleString()} characters (preamble: {preamble.length.toLocaleString()}, document: {docContent.length.toLocaleString()})
        </span>
        <span className="text-xs text-muted-foreground">
          Powered by BahaTeX (Beta) for ComTrad
        </span>
      </footer>
    </div>
  )
}
